import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@features/auth';
import type { UserRole } from '@features/auth';
import { useWorkspaceAccessStore } from '@/shared/stores/workspace-access.store';
import { AuthFormSkeleton } from '@/shared/components/ui/skeletons';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  /** If true, this route is accessible even when workspace access is blocked */
  allowWhenBlocked?: boolean;
}

// Routes that are always accessible even when blocked
const ALWAYS_ALLOWED_ROUTES = [
  '/configuracion',
  '/configuracion/suscripcion',
  '/perfil',
  '/dashboard', // Dashboard shows limited info when blocked
];

export function ProtectedRoute({ children, allowedRoles, allowWhenBlocked }: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { isAccessBlocked } = useWorkspaceAccessStore();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return <AuthFormSkeleton />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Admin goes to pagos, others to dashboard
    const fallback = user.role === 'admin' ? '/admin/pagos' : '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  // Check if workspace access is blocked (only for non-admin users)
  if (isAccessBlocked && user?.role !== 'admin') {
    const currentPath = location.pathname;
    const isRouteAllowed = allowWhenBlocked ||
      ALWAYS_ALLOWED_ROUTES.some(route => currentPath.startsWith(route));

    if (!isRouteAllowed) {
      // Redirect to settings page where they can renew subscription
      return <Navigate to="/configuracion" replace />;
    }
  }

  return <>{children}</>;
}
